import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import './CommunityContentDisplay.scss';
import { CommunityContext } from '../../../../contexts/ContextCreator';
import DeletedPost from '../../DeletedPost/DeletedPost';
import EditorialPodcastSectionForSingleContent from '../../EditorialPodcastSectionForSingleContent/EditorialPodcastSectionForSingleContent';
import UnifiedPostCard from '../../../UnifiedPostCard/UnifiedPostCard';
import { renderReactionBar } from '../../../../pages/Community/utils';
import CommonCardShell from '../../../Layout/CommonCardShell/CommonCardShell';
import { singleCommentPropTypes } from '../../../../pages/defaultProps';
import CommentList from '../../CommentBox/CommentList/CommentList';
import CommentBox from '../../CommentBox/CommentBox';
import CardHeader from '../../../Groups/CardHeader/CardHeader';

const CommunityContentDisplay = (props) => {
    const {
        getContent,
        pageProps,
        submitComment,
        userCommunityData,
        openedCommentHash,
    } = useContext(CommunityContext);
    const content = getContent(props.content.hash);
    const hasComment = content.commentCount;
    const isDeletedContent = !!(
        content.moderationOption &&
        content.moderationOption.status &&
        content.moderationOption.status.type === 'delete'
    );
    const { session } = pageProps;
    const { username, logo } = session;
    const authorImage = {
        src: logo || '/v1/avatar_black_160_vtg7lw.svg',
        alt: `${username || 'default'} img`,
    };
    const isVip = !!userCommunityData?.isVip;

    const renderCommentSection = () => (
        <CommentList hash={content.hash} location={content.location} />
    );
    const renderCommentBox = () => {
        const placeHolderText = hasComment
            ? 'Join the conversation...'
            : 'Be the first to comment...';

        const showExpanded = openedCommentHash === content.hash;
        return (
            <CommentBox
                onSubmit={(hash, comment, isAnon) => {
                    submitComment(hash, comment, isAnon);
                }}
                isVip={isVip}
                parentHash={content.hash}
                authorImage={authorImage}
                placeholderText={placeHolderText}
                showExpanded={showExpanded}
            />
        );
    };
    const location = content.group ? 'singleGroupContent' : 'singleContent';
    const UnifiedPostCardReactionBar = renderReactionBar(
        content.hash,
        location,
        'left-aligned-bar'
    );

    const contentIsEditorialOrPodcast =
        content.type === 'editorial-content' ||
        content.type === 'community-podcast';

    const communityContent = () => {
        const groupHeader = content.group ? (
            <div className="group-post-header">
                <CardHeader>Group Post</CardHeader>
            </div>
        ) : null;

        let DisplayedContent = (
            <UnifiedPostCard
                {...content}
                className="single-content-postcard"
                isSingleContent
                singleContentReactionBar={UnifiedPostCardReactionBar}
                feedType="masterFeed"
            />
        );

        if (contentIsEditorialOrPodcast) {
            DisplayedContent = (
                <EditorialPodcastSectionForSingleContent
                    currentTopic={props.currentTopic}
                    content={content}
                    showReactionBar={!props.hideReactionBar}
                    location="singleContent"
                />
            );
        }

        const editorialStyling = contentIsEditorialOrPodcast
            ? 'editorial-styling'
            : '';
        const paddingStyling = props?.content?.group
            ? 'group-styling'
            : 'no-padding';
        return (
            <CommonCardShell
                className={`${paddingStyling} ${editorialStyling}`}
            >
                {groupHeader}
                {DisplayedContent}
                {contentIsEditorialOrPodcast && (
                    <div className="comments-section">
                        {renderCommentBox()}
                        {renderCommentSection(content)}
                    </div>
                )}
                {props?.content?.group?.groupUrl ? (
                    <a
                        href={props?.content?.group?.groupUrl}
                        className="back-to-groups-link"
                    >
                        Back to group page
                    </a>
                ) : null}
            </CommonCardShell>
        );
    };

    return (
        <div className="component-Community-SingleContent-CommunityContentDisplay">
            {isDeletedContent ? (
                <DeletedPost groupUrl={props?.content?.group?.groupUrl} />
            ) : (
                communityContent()
            )}
        </div>
    );
};

CommunityContentDisplay.propTypes = {
    /**
     * Content props
     */
    content: PropTypes.shape({
        type: PropTypes.string,
        hash: PropTypes.string,
        lastId: PropTypes.number,
        ancestorHash: PropTypes.string,
        commentCount: PropTypes.number,
        comments: PropTypes.arrayOf(PropTypes.shape(singleCommentPropTypes)),
        group: PropTypes.shape({
            groupUrl: PropTypes.string,
        }),
    }).isRequired,
    /**
     * Current Topic needed for Editorial Content and Podcast
     */
    currentTopic: PropTypes.string,
    /**
     * Whether to hide reaction bar for Editorial Content and Podcast.
     */
    hideReactionBar: PropTypes.bool,
};

CommunityContentDisplay.defaultProps = {
    currentTopic: null,
    hideReactionBar: false,
};

CommunityContentDisplay.displayName = 'CommunityContentDisplay';

export default React.memo(CommunityContentDisplay);
